'use client';

import Link from 'next/link';
import { useLanguage } from '@/hooks/use-language';

export default function PrivacyPage() {
  const { lang } = useLanguage();
  const isAr = lang === 'ar';

  return (
    <div className="min-h-screen bg-background text-white" dir={isAr ? 'rtl' : 'ltr'}>
      <header className="sticky top-0 z-50 border-b border-zinc-900 bg-background/80 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center">
          <Link href="/" className="text-lg font-bold">Momira</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 space-y-8 text-sm leading-relaxed text-zinc-300">
        <h1 className="text-3xl font-bold text-white">{isAr ? 'سياسة الخصوصية' : 'Privacy Policy'}</h1>
        <p className="text-zinc-500 text-xs">{isAr ? 'آخر تحديث: مايو 2025' : 'Last updated: May 2025'}</p>

        {isAr ? (
          <>
            <section>
              <h2 className="text-lg font-semibold text-white mb-2">ملكية الصور والفيديوهات</h2>
              <p>الصور والفيديوهات التي يرفعها الضيوف هي ملك لصاحب المناسبة والضيوف أنفسهم. لا تدّعي Momira أي ملكية لهذا المحتوى.</p>
            </section>
            <section>
              <h2 className="text-lg font-semibold text-white mb-2">لا نستخدم محتواك للتسويق أو الذكاء الاصطناعي</h2>
              <p>لا نستخدم صور أو فيديوهات مناسباتك للتسويق، أو للعرض العام، أو لتدريب نماذج الذكاء الاصطناعي — بدون موافقة صريحة منك أو من صاحب المناسبة.</p>
            </section>
            <section>
              <h2 className="text-lg font-semibold text-white mb-2">الروابط الخاصة</h2>
              <p>روابط المناسبات الخاصة لا تظهر في محركات البحث. فقط من يملك الرابط أو رمز QR يمكنه الوصول.</p>
            </section>
            <section>
              <h2 className="text-lg font-semibold text-white mb-2">الخصوصية الافتراضية</h2>
              <p>كل مناسبة خاصة افتراضيًا. الصور لا تظهر للضيوف الآخرين إلا إذا سمح صاحب المناسبة بذلك.</p>
            </section>
            <section>
              <h2 className="text-lg font-semibold text-white mb-2">مدة التخزين</h2>
              <p>يتم حذف الملفات تلقائيًا بعد انتهاء مدة الباقة (14 يومًا، 30 يومًا، أو 90 يومًا). يمكن لصاحب المناسبة طلب الحذف الفوري.</p>
            </section>
            <section>
              <h2 className="text-lg font-semibold text-white mb-2">البيانات التقنية</h2>
              <p>قد نحفظ بعض البيانات التقنية مثل عنوان IP وUser-Agent لأغراض الحماية ومنع الإساءة فقط. لا نشارك هذه البيانات مع أطراف ثالثة.</p>
            </section>
            <section>
              <h2 className="text-lg font-semibold text-white mb-2">حذف المناسبة</h2>
              <p>يمكن لصاحب المناسبة حذف المناسبة وجميع ملفاتها في أي وقت من لوحة التحكم.</p>
            </section>
            <section>
              <h2 className="text-lg font-semibold text-white mb-2">التواصل</h2>
              <p>لأي استفسار عن خصوصيتك، تواصل معنا عبر <Link href="/contact" className="text-amber-300 hover:underline">صفحة التواصل</Link>.</p>
            </section>
          </>
        ) : (
          <>
            <section>
              <h2 className="text-lg font-semibold text-white mb-2">Ownership of Photos & Videos</h2>
              <p>Photos and videos uploaded by guests belong to the event host and the guests themselves. Momira claims no ownership over this content.</p>
            </section>
            <section>
              <h2 className="text-lg font-semibold text-white mb-2">No Marketing or AI Training Use</h2>
              <p>We do not use your event photos or videos for marketing, public display, or AI model training — without your explicit consent.</p>
            </section>
            <section>
              <h2 className="text-lg font-semibold text-white mb-2">Private Links</h2>
              <p>Private event links are not indexed by search engines. Only those with the link or QR code can access the event.</p>
            </section>
            <section>
              <h2 className="text-lg font-semibold text-white mb-2">Private by Default</h2>
              <p>Every event is private by default. Photos are not visible to other guests unless the host explicitly enables this.</p>
            </section>
            <section>
              <h2 className="text-lg font-semibold text-white mb-2">Storage Duration</h2>
              <p>Files are automatically deleted after your plan's retention period (14, 30, or 90 days). Hosts can also request immediate deletion.</p>
            </section>
            <section>
              <h2 className="text-lg font-semibold text-white mb-2">Technical Data</h2>
              <p>We may store limited technical data such as IP addresses and User-Agent strings for security and abuse prevention only. We do not share this with third parties.</p>
            </section>
            <section>
              <h2 className="text-lg font-semibold text-white mb-2">Event Deletion</h2>
              <p>The event host can delete the event and all its files at any time from the dashboard.</p>
            </section>
            <section>
              <h2 className="text-lg font-semibold text-white mb-2">Contact</h2>
              <p>For any privacy inquiries, contact us via the <Link href="/contact" className="text-amber-300 hover:underline">contact page</Link>.</p>
            </section>
          </>
        )}
      </main>

      <footer className="py-8 px-6 border-t border-zinc-900 text-center text-sm text-zinc-500">
        <div className="flex flex-wrap justify-center gap-4">
          <Link href="/privacy" className="hover:text-zinc-300">{isAr ? 'الخصوصية' : 'Privacy'}</Link>
          <Link href="/terms" className="hover:text-zinc-300">{isAr ? 'الشروط' : 'Terms'}</Link>
          <Link href="/contact" className="hover:text-zinc-300">{isAr ? 'تواصل معنا' : 'Contact'}</Link>
        </div>
      </footer>
    </div>
  );
}
